<template>
  <div
    ref="cell"
    class="thumb landscape"
    @mouseenter.prevent="enterMouse"
    @mouseleave.prevent="leaveMouse"
  >
    <div class="btn-ui">
      <template v-if="!isLoading && !isPause">
        <button
          v-if="isPlayerMute"
          class="btn-volume-off"
          @click.prevent="() => setMuteState(false)"
        >
          volume off
        </button>
        <button
          v-else
          class="btn-volume-on"
          @click.prevent="() => setMuteState(true)"
        >
          volume on
        </button>
      </template>
    </div>

    <div :class="isShowSpinner ? 'spinner on' : 'spinner'">
      <div class="loading">loading</div>
    </div>

    <a href="#" @click.prevent="goDetailPage(cellInfo)">
      <div class="video-area-wrap">
        <div :id="`video-${channelId}`" ref="player" class="video-area"></div>
        <age-limits :target-age="targetAge" :age-tag="ageTag" />
      </div>
      <div class="thumb-image">
        <ul class="badge-list">
          <li class="left-top-area">
            <ul v-top-tag-list="topTagList"></ul>
          </li>
        </ul>
        <div class="picture-area">
          <WebpImage
            :image-info="{
              imageUrl: cellInfo.thumbnail,
              width: thumbnailInfo.width,
              height: thumbnailInfo.height,
              alt: altTitle,
              className: 'thumb-img',
            }"
          />
        </div>
        <!-- [D] 이미지가 등록되지 않았거나, 오류일 경우, display: flex / aria-hidden="false" 해주세요 -->
        <div
          class="no-thumb"
          :aria-hidden="!isThumbnail"
          :style="`display: ${isThumbnail ? 'none' : 'flex'}`"
        >
          <span class="alt-text">{{ alt }}</span>
        </div>
        <div v-if="hasProgress" class="cell-progress">
          <span
            class="gradation-purple"
            :style="'width:' + Number(progress) * 100 + '%;'"
          ></span>
        </div>
        <age-limits :target-age="targetAge" :age-tag="ageTag" />
      </div>
      <div class="sub-title">
        <div
          ref="title"
          v-title-list="{
            titleList: cellInfo.title_list,
            cellType: 'landscape',
            callback: loaded,
          }"
          class="sub-right"
        ></div>
      </div>
    </a>
  </div>
</template>

<script>
  import _ from 'lodash'
  import shave from 'shave'
  import AgeLimits from '@/components/model/AgeLimits'
  import {
    titleList,
    topTagList,
    commonTagList,
  } from '@/common/resources/directives'
  import CellMixIn from '@/mixins/cell.mixin'
  import { resizeThumbnail } from '@/common/utils/image'
  import fp from 'lodash/fp'
  import { getCellEventObject } from '@/common/utils/gnb.fp'
  import { parseUrl } from 'query-string'
  import {
    EVENT_TYPE_MOUSE_ENTER,
    EVENT_TYPE_MOUSE_LEAVE,
  } from '@/common/resources/const'
  import Player from '@/common/player/scalableplayer'
  import { isAdultContent } from '@/common/utils/adult'
  import cookie from '@/common/utils/cookie'
  import WebpImage from '@/components/image/WebpImage'
  export default {
    name: 'LandscapePlayerCell',
    components: {
      WebpImage,
      AgeLimits,
    },
    directives: {
      titleList,
      topTagList,
      commonTagList,
    },
    mixins: [CellMixIn],
    props: {
      cellInfo: {
        type: Object,
      },
      thumbnailInfo: {
        type: Object,
      },
      isPlayerMute: {
        type: Boolean,
      },
      setPlayerMuteStateFn: {
        type: Function,
      },
    },
    data() {
      return {
        player: null,
        isLoading: false,
        isShowSpinner: false,
        isPause: false,
        spinnerTimer: null,
      }
    },
    computed: {
      getSrc() {
        return resizeThumbnail(this.cellInfo.thumbnail, {
          width: this.thumbnailInfo.width,
          height: this.thumbnailInfo.height,
        })
      },
      channelId() {
        const { onNavigation } = fp.flow(
          fp.get('event_list'),
          getCellEventObject,
        )(this.cellInfo)
        const url = _.get(onNavigation, 'url', '')
        const { query } = parseUrl(url)
        return _.get(query, 'channelid', '')
      },
    },
    beforeDestroy() {
      this.disableVideoMode()
    },
    methods: {
      loaded(el) {
        this.$nextTick(() => {
          _.forEach(el.querySelectorAll('.line1'), (line1El) => {
            shave(line1El, 28)
          })
        })
      },
      goDetailPage(cellInfo) {
        if (this.player && this.player.destructor) {
          this.player.destructor()
        }
        this.goDetail(cellInfo)
      },
      setMuteState(isMute) {
        this.setPlayerMuteStateFn(isMute)
        this.player.setMute(isMute)
      },
      enterMouse() {
        this.$liveGnbEvents.$emit(EVENT_TYPE_MOUSE_ENTER, this)
      },
      leaveMouse() {
        this.$liveGnbEvents.$emit(EVENT_TYPE_MOUSE_LEAVE, this)
      },
      clearSpinner() {
        clearTimeout(this.spinnerTimer)
        this.isShowSpinner = false
      },
      doPlay() {
        if (this.player !== null) return
        if (!this.$refs.cell) return
        this.isLoading = true
        this.spinnerTimer = setTimeout(() => {
          this.isShowSpinner = true
        }, 500)
        this.player = null

        this.player = new Player(this.$refs.player, 'i', 'i')
        this.player.setInitImage(this.getSrc)
        this.player.setTimeoutCallback(() => {
          this.isPause = true
        })
        this.player.setLoadedCallback(() => {
          if (this.player === null || !(this.$refs && this.$refs.cell)) {
            return this.clearSpinner()
          }

          this.isLoading = false
          this.clearSpinner()
          this.$refs.cell.classList.add('playing')
          this.player.setMute(this.isPlayerMute)
          this.$refs.cell.classList.add('video-mode')
          this.$refs.cell.parentNode.classList.add('expand')
        })
        this.player.setErrorCallback(() => {
          this.disableVideoMode()
        })
        this.player.load('live', this.channelId)
      },
      blockPlayer() {
        if (this.player !== null) return
        if (!this.$refs.cell) return
        this.player = null
        this.$refs.cell.classList.add('video-mode')
        this.$refs.cell.parentNode.classList.add('expand')
        this.player = new Player(this.$refs.player, 'i', 'i')
        this.player.setInitImage(this.getSrc)
      },
      enableVideoMode() {
        if (
          isAdultContent(this.targetAge) &&
          cookie.isNotVerifiedAdultUser() &&
          cookie.getItem('adult-settings') !== 'N'
        ) {
          this.blockPlayer()
        } else {
          this.doPlay()
        }
      },
      disableVideoMode() {
        if (this.$refs.cell && this.$refs.cell.parentNode) {
          this.$refs.cell.parentNode.classList.remove('expand')
        }
        this.isLoading = false
        this.clearSpinner()
        if (this.player !== null) {
          this.player.destructor()
          this.player = null
        }
        if (this.$refs.cell && this.$refs.cell.classList) {
          this.$refs.cell.classList.remove('video-mode')
          this.$refs.cell.classList.remove('playing')
        }
      },
    },
  }
</script>

<style lang="scss" src="@/assets/scss/import/landscape.scss"></style>
<style lang="css">
  .live-ui .thumb-image .thumb-img,
  .live-ui .thumb-image:hover .thumb-img {
    -webkit-transition: none;
    transition: none;
    -webkit-transform: none;
    transform: none;
  }

  .live-ui .player_container,
  .live-ui .bg-image,
  .live-ui .video-area .cell-progress,
  .live-ui .btn-ui {
    -webkit-transition: opacity 0.5s linear 0.5s;
    -moz-transition: opacity 0.5s linear 0.5s;
    transition: opacity 0.5s linear 0.5s;
  }

  .live-ui .bg-image {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 10;
    background-repeat: no-repeat;
    background-size: 100%;
  }

  .live-ui .player_container {
    width: 100%;
    height: 100%;
  }

  .live-ui .playing .bg-image,
  .live-ui .player_container,
  .live-ui .video-area .cell-progress,
  .live-ui .btn-ui {
    opacity: 0;
  }

  .live-ui .bg-image,
  .live-ui .playing .player_container,
  .live-ui .playing .video-area .cell-progress,
  .live-ui .playing .btn-ui {
    opacity: 1;
  }
</style>

<style lang="css" scoped>
  .multi .swiper-slide span.creator-name {
    margin: -2px 0 0;
  }

  /* live-ui */
  .live-ui .landscape {
    overflow: hidden;
  }

  .live-ui .landscape.video-mode {
    width: 385px;
    height: 217px;
  }

  .live-ui .landscape a {
    height: 100%;
  }

  .live-ui .landscape .thumb-image {
    width: 100%;
    transform: none;
  }

  /*video-area*/
  .live-ui .video-area-wrap {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    width: 100%;
    height: 100%;
    overflow: hidden;
    text-align: center;
  }

  .live-ui .video-area {
    overflow: hidden;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: 12px;
  }

  .live-ui .landscape.video-mode .video-area-wrap {
    z-index: 10;
  }

  .live-ui .video-area-wrap .tag-age {
    z-index: 10;
  }

  .live-ui .landscape .thumb-image,
  .live-ui .landscape .sub-title {
    opacity: 1;
  }

  .live-ui .landscape.video-mode .thumb-image,
  .live-ui .landscape.video-mode .sub-title {
    -webkit-transition: opacity 0.5s linear;
    -moz-transition: opacity 0.5s linear;
    transition: opacity 0.5s linear;
    opacity: 0;
  }

  .live-ui .landscape.video-mode .video-area video {
    position: relative;
    z-index: 1;
  }

  .live-ui .landscape.video-mode .cell-progress {
    display: block;
  }

  .live-ui .btn-ui,
  .live-ui .spinner {
    position: absolute;
    right: 8px;
    bottom: 12px;
    width: 30px;
    height: 30px;
    z-index: 100;
  }

  .live-ui .spinner {
    display: none;
  }

  .live-ui .spinner.on {
    display: block;
  }

  .live-ui .btn-ui button {
    display: none;
    width: 30px;
    height: 30px;
    font-size: 0;
    text-indent: -99999px;
  }

  .live-ui .btn-ui .btn-volume-on {
    background-image: url(../../../assets/images/common/ic_volume_on.png);
    background-size: cover;
  }

  .live-ui .btn-ui .btn-volume-off {
    background-image: url(../../../assets/images/common/ic_volume_off.png);
    background-size: cover;
  }

  .live-ui .landscape.video-mode .btn-ui .btn-volume-on,
  .live-ui .landscape.video-mode .btn-ui .btn-volume-off {
    display: block;
  }

  .list-view-detail .live-ui .landscape {
    float: none;
    display: inline-block;
    margin-bottom: 0;
    vertical-align: top;
  }

  .live-ui .landscape .sub-title {
    text-align: left;
  }

  .live-ui .video-area .cell-progress {
    display: none;
    z-index: 1;
    background-color: rgba(0, 0, 0, 0.1);
  }

  .spinner {
    width: 30px;
    height: 30px;
    font-size: 0;
    text-indent: -99999px;
    animation-iteration-count: infinite;
  }
  .loading {
    width: 30px;
    height: 30px;
    background: url(../../../assets/images/common/loading_white.png) no-repeat 0
      0;
    background-size: cover;
    -webkit-animation: box 1s steps(12) infinite;
    -moz-animation: box 1s steps(12) infinite;
    animation: box 1s steps(12) infinite;
  }

  @-webkit-keyframes box {
    from {
      background-position: 0 0;
    }
    to {
      background-position: -360px 0;
    }
  }

  @-moz-keyframes box {
    from {
      background-position: 0 0;
    }
    to {
      background-position: -360px 0;
    }
  }

  @keyframes box {
    from {
      background-position: 0 0;
    }
    to {
      background-position: -360px 0;
    }
  }
</style>
